import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { fetchproduct } from "../../../services/api/Product";
import Footer from "../../common/Footer/Footer";
import { useSelector } from "react-redux";

const PAGE_SIZE = 20;

// ─── Skeleton Card ────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <Box sx={{ borderRadius: "14px", overflow: "hidden", border: "1.5px solid #eaeef5" }}>
    <Skeleton variant="rectangular" height={160} sx={{ bgcolor: "#f0f4f8" }} />
    <Box sx={{ p: 1.2 }}>
      <Skeleton width="90%" height={14} sx={{ mb: 0.5, bgcolor: "#f0f4f8" }} />
      <Skeleton width="60%" height={12} sx={{ mb: 0.5, bgcolor: "#f0f4f8" }} />
      <Skeleton width="40%" height={16} sx={{ bgcolor: "#f0f4f8" }} />
    </Box>
  </Box>
);

// ─── Product Card ─────────────────────────────────────────────────────────────

const ProductCard = React.memo(({ product, onProductClick }) => {
  const discountPct =
    product.mrp && product.price && product.mrp > product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : null;

  const handleClick = useCallback(() => {
    const uuid = product.uuid || product.productUuid || product.id;
    if (uuid && onProductClick) onProductClick(uuid);
  }, [product, onProductClick]);

  return (
    <Card
      onClick={handleClick}
      sx={{
        border: "1.5px solid #eaeef5",
        borderRadius: "14px",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.22s ease",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 32px rgba(15,76,129,0.12)",
          borderColor: "#c5d4ee",
        },
      }}
    >
      {discountPct > 0 && (
        <Chip
          label={`${discountPct}% OFF`}
          size="small"
          sx={{
            position: "absolute", top: 8, left: 8, zIndex: 2,
            fontSize: 9, fontWeight: 800, height: 20,
            background: "#fff3e0", color: "#e65100",
          }}
        />
      )}

      <Box
        sx={{
          height: 160,
          background: "#FEFEFE",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {product.imageUrl ? (
          <CardMedia
            component="img"
            image={product.imageUrl}
            alt={product.productName || product.name}
            sx={{ width: "80%", height: "80%", objectFit: "contain" }}
          />
        ) : (
          <Typography sx={{ fontSize: 48 }}>📦</Typography>
        )}
      </Box>

      <CardContent sx={{ p: "10px !important", display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: 12.5, fontWeight: 700, color: "#1a1a2e", lineHeight: 1.35,
            display: "-webkit-box", WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical", overflow: "hidden",
          }}
        >
          {product.productName || product.name}
        </Typography>
        {product.unit && (
          <Typography sx={{ fontSize: 11, color: "#999", fontWeight: 600 }}>
            {product.unit}
          </Typography>
        )}
        {product.price > 0 && (
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f4c81" }}>
            ₹{product.price}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});

// ─── ProductsGrid ─────────────────────────────────────────────────────────────

const ProductsGrid = ({ categoryId, onProductClick }) => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const zipcode = useSelector((state) => state.orders?.postalcode);


  // Guards against double-fetching
  const isFetchingRef = useRef(false);
  // Tracks which category is currently active
  const activeCategoryRef = useRef(null);
  // Sentinel div watched by IntersectionObserver
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  // ── API call ──────────────────────────────────────────────────────────────
  const fetchPage = useCallback(async (catId, pageNum, isFirst) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    isFirst ? setInitialLoading(true) : setLoadingMore(true);

    try {
      const response = await fetchproduct({
        categoryId: catId,
        postalCode: zipcode ? String(zipcode) : "",
        page: pageNum,          // page number (1-based)
        pageSize: PAGE_SIZE,    // how many per page
        // If your API uses offset/limit instead, replace with:
        // offset: (pageNum - 1) * PAGE_SIZE,
        // limit: PAGE_SIZE,
      });
      console.log("see the payload", String(zipcode));

      const data = response?.data?.data;
      const newItems = data?.products || [];
      // Support common total-count field names from APIs
      const total = data?.totalCount ?? data?.total ?? data?.count ?? null;

      if (isFirst) {
        setProducts(newItems);
      } else {
        setProducts((prev) => [...prev, ...newItems]);
      }

      if (total !== null) setTotalCount(total);

      // Determine if more pages exist
      const fetchedSoFar = (pageNum - 1) * PAGE_SIZE + newItems.length;
      const more = newItems.length === PAGE_SIZE && (total === null || fetchedSoFar < total);
      setHasMore(more);
      setPage(pageNum + 1);
    } catch (err) {
      console.error("ProductsGrid fetch error:", err);
      setHasMore(false);
    } finally {
      isFetchingRef.current = false;
      setInitialLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // ── Reset and fetch page 1 when category changes ──────────────────────────
  useEffect(() => {
    if (!categoryId) {
      setProducts([]);
      setPage(1);
      setHasMore(true);
      setTotalCount(0);
      activeCategoryRef.current = null;
      isFetchingRef.current = false;
      return;
    }

    if (activeCategoryRef.current === categoryId) return; // same category, skip

    // New category — full reset then fetch
    activeCategoryRef.current = categoryId;
    isFetchingRef.current = false;
    setProducts([]);
    setPage(1);
    setHasMore(true);
    setTotalCount(0);
    fetchPage(categoryId, 1, true);
  }, [categoryId, fetchPage]);

  // ── Load next page ─────────────────────────────────────────────────────────
  const loadMore = useCallback(() => {
    if (!hasMore || isFetchingRef.current || !activeCategoryRef.current) return;
    fetchPage(activeCategoryRef.current, page, false);
  }, [hasMore, page, fetchPage]);

  // ── Attach IntersectionObserver to sentinel ───────────────────────────────
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!hasMore || initialLoading) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      {
        // 200px early trigger: starts loading before user hits the very bottom
        rootMargin: "0px 0px 200px 0px",
        threshold: 0,
      }
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loadMore, hasMore, initialLoading]);

  if (!categoryId) return null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Products section ── */}
      <Box sx={{ p: 2 }}>
        {/* Title + count */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#1a1a2e" }}>
            Products
          </Typography>
          {!initialLoading && (totalCount > 0 || products.length > 0) && (
            <Chip
              label={totalCount > 0 ? `${totalCount} items` : `${products.length} loaded`}
              size="small"
              sx={{ fontSize: 11, fontWeight: 700, background: "#f0f4ff", color: "#0f4c81" }}
            />
          )}
        </Box>

        {/* Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 1.8,
          }}
        >
          {/* Page 1 loading skeletons */}
          {initialLoading &&
            Array(PAGE_SIZE).fill(0).map((_, i) => <SkeletonCard key={`sk-${i}`} />)
          }

          {/* Rendered products */}
          {!initialLoading && products.map((p, i) => (
            <ProductCard
              key={`${p.id || p.productId}-${i}`}
              product={p}
              onProductClick={onProductClick}
            />
          ))}

          {/* Empty state */}
          {!initialLoading && products.length === 0 && (
            <Box sx={{ gridColumn: "1/-1", textAlign: "center", py: 8, color: "#aaa" }}>
              <Typography sx={{ fontSize: 48 }}>🛒</Typography>
              <Typography sx={{ fontWeight: 700, mt: 1 }}>No products found</Typography>
            </Box>
          )}
        </Box>

        {/* "Loading more" spinner — shown while next page fetches */}
        {loadingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4, gap: 1.5 }}>
            <CircularProgress size={22} sx={{ color: "#0f4c81" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f4c81" }}>
              Loading more products...
            </Typography>
          </Box>
        )}

        {/* Invisible sentinel — triggers loadMore when scrolled into view */}
        {!initialLoading && hasMore && !loadingMore && (
          <Box ref={sentinelRef} sx={{ height: 1 }} />
        )}
      </Box>

      {/* ── Footer: renders naturally at bottom once all products are loaded ── */}
      {!initialLoading && !hasMore && products.length > 0 && (
        <>
          <Box sx={{ textAlign: "center", py: 2, mx: 2, borderTop: "1px solid #f0f0f0" }}>
            <Typography sx={{ fontSize: 12, color: "#bbb", fontWeight: 600 }}>
              ✓ All {products.length} products shown
            </Typography>
          </Box>
          {/* <Footer /> */}
        </>
      )}
    </Box>
  );
};

export default ProductsGrid;